import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrency } from '@/hooks/useCurrency';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Zap,
  FileText,
} from 'lucide-react';
import type { TransactionTemplate } from '@/lib/types';

interface TemplateListProps {
  templates: TransactionTemplate[];
  isLoading: boolean;
  onEdit: (template: TransactionTemplate) => void;
  onDelete: (id: string) => void;
  onQuickCreate: (template: TransactionTemplate) => void;
  onView: (template: TransactionTemplate) => void;
}

const typeColors: Record<string, string> = {
  expense: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  income: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center"
      data-testid="template-empty-state"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <FileText className="h-6 w-6 text-muted-foreground" />
      </div>
      <div>
        <p className="font-medium">No templates yet</p>
        <p className="text-sm text-muted-foreground">
          Create reusable templates to quickly add recurring transactions.
        </p>
      </div>
      <Button size="sm" className="min-h-[44px]" onClick={onAdd} data-testid="btn-new-template">
        <Plus className="mr-1.5 h-4 w-4" />
        Create Template
      </Button>
    </div>
  );
}

export function TemplateList({
  templates,
  isLoading,
  onEdit,
  onDelete,
  onQuickCreate,
  onView,
}: TemplateListProps) {
  const { fmt } = useCurrency();

  if (isLoading) return <LoadingSkeleton />;

  if (templates.length === 0) {
    return <EmptyState onAdd={() => onEdit(null as unknown as TransactionTemplate)} />;
  }

  const renderDropdown = (tpl: TransactionTemplate) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Manage ${tpl.name}`}
          className="min-h-[44px] min-w-[44px]"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          data-testid={`template-edit-${tpl._id}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(tpl);
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive"
          data-testid={`template-delete-${tpl._id}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(tpl._id);
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {/* Desktop table */}
      <Table className="hidden md:table" data-testid="template-list-table">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Default Amount</TableHead>
            <TableHead className="hidden lg:table-cell">Tags</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((tpl) => (
            <TableRow
              key={tpl._id}
              data-testid={`template-row-${tpl._id}`}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onView(tpl)}
            >
              <TableCell className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-lg shrink-0">
                  {tpl.emoji || tpl.name.charAt(0).toUpperCase()}
                </div>
                <div className="font-medium">{tpl.name}</div>
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={`capitalize ${typeColors[tpl.type || 'expense'] || ''}`}
                >
                  {tpl.type || 'expense'}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-semibold">
                {tpl.amount != null ? fmt(tpl.amount) : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                {tpl.tags?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {tpl.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {tpl.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{tpl.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[36px]"
                    data-testid={`template-quick-create-${tpl._id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickCreate(tpl);
                    }}
                  >
                    <Zap className="mr-1.5 h-3.5 w-3.5" />
                    Use
                  </Button>
                  {renderDropdown(tpl)}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2" data-testid="template-list-cards">
        {templates.map((tpl) => (
          <div
            key={tpl._id}
            data-testid={`template-card-${tpl._id}`}
            className="flex items-start gap-3 rounded-lg border bg-card p-3 cursor-pointer hover:bg-muted/50 transition-colors active:scale-[0.98] min-h-[44px]"
            onClick={() => onView(tpl)}
          >
            {/* Emoji avatar */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg">
              {tpl.emoji || tpl.name.charAt(0).toUpperCase()}
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm leading-tight break-words">
                {tpl.name}
              </p>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                <Badge
                  variant="outline"
                  className={`capitalize text-[10px] px-1.5 py-0 ${typeColors[tpl.type || 'expense'] || ''}`}
                >
                  {tpl.type || 'expense'}
                </Badge>
                <span className="text-xs text-muted-foreground font-semibold">
                  {tpl.amount != null ? fmt(tpl.amount) : 'No default'}
                </span>
              </div>
              {tpl.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {tpl.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                      {tag}
                    </Badge>
                  ))}
                  {tpl.tags.length > 3 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      +{tpl.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] min-w-[44px] px-2"
                data-testid={`template-quick-create-${tpl._id}`}
                onClick={() => onQuickCreate(tpl)}
              >
                <Zap className="h-4 w-4" />
              </Button>
              {renderDropdown(tpl)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
